'use client';
import React from 'react';
import { useRouter } from 'next/navigation';
import { MapPin, Briefcase, Clock, ChevronRight, Truck, Star, Lock } from 'lucide-react';

export interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  province: string;
  licenseRequired: string;
  pdpRequired: boolean;
  salary: string;
  jobType: string;
  postedDaysAgo: number;
  urgent: boolean;
  description: string;
}

export const allAvailableJobs: Job[] = [
{
  id: 'job-001',
  title: 'Code 14 Long-Haul Truck Driver',
  company: 'Imperial Logistics',
  location: 'Johannesburg, Gauteng',
  province: 'Gauteng',
  licenseRequired: 'Code 14 (EC)',
  pdpRequired: true,
  salary: 'R18,000 – R24,000/month',
  jobType: 'Full-Time',
  postedDaysAgo: 1,
  urgent: true,
  description: 'Drive articulated trucks on national routes between Johannesburg and Cape Town. Night allowances included.'
},
{
  id: 'job-002',
  title: 'Code 10 Rigid Truck Driver',
  company: 'Bidvest Panalpina Logistics',
  location: 'Durban, KwaZulu-Natal',
  province: 'KwaZulu-Natal',
  licenseRequired: 'Code 10',
  pdpRequired: true,
  salary: 'R14,000 – R18,000/month',
  jobType: 'Full-Time',
  postedDaysAgo: 2,
  urgent: false,
  description: 'Deliver goods across KwaZulu-Natal and Eastern Cape. Experience with refrigerated trucks advantageous.'
},
{
  id: 'job-003',
  title: 'Tanker Driver – Fuel & Chemicals',
  company: 'Vopak South Africa',
  location: 'Cape Town, Western Cape',
  province: 'Western Cape',
  licenseRequired: 'Code 14 (EC)',
  pdpRequired: true,
  salary: 'R22,000 – R28,000/month',
  jobType: 'Full-Time',
  postedDaysAgo: 3,
  urgent: true,
  description: 'Transport petroleum products and chemicals. Hazmat certification required. Excellent benefits package.'
},
{
  id: 'job-004',
  title: 'Bus Driver – Intercity Routes',
  company: 'Greyhound / FlixBus SA',
  location: 'Pretoria, Gauteng',
  province: 'Gauteng',
  licenseRequired: 'Code 14 (EC)',
  pdpRequired: true,
  salary: 'R16,000 – R20,000/month',
  jobType: 'Full-Time',
  postedDaysAgo: 4,
  urgent: false,
  description: 'Drive intercity passenger buses on Johannesburg–Durban and Johannesburg–Cape Town routes.'
},
{
  id: 'job-005',
  title: 'Mining Dump Truck Operator',
  company: 'Anglo American Platinum',
  location: 'Rustenburg, North West',
  province: 'North West',
  licenseRequired: 'Code 14 (EC)',
  pdpRequired: false,
  salary: 'R20,000 – R26,000/month',
  jobType: 'Contract (12 months)',
  postedDaysAgo: 5,
  urgent: false,
  description: 'Operate heavy mining dump trucks at platinum mine. Accommodation and meals provided on site.'
},
{
  id: 'job-006',
  title: 'Refrigerated Truck Driver',
  company: 'Clover Industries',
  location: 'Bloemfontein, Free State',
  province: 'Free State',
  licenseRequired: 'Code 10',
  pdpRequired: true,
  salary: 'R13,500 – R17,000/month',
  jobType: 'Full-Time',
  postedDaysAgo: 6,
  urgent: false,
  description: 'Deliver dairy and food products across Free State and Northern Cape. Reefer experience required.'
},
{
  id: 'job-007',
  title: 'Tipper Truck Driver – Construction',
  company: 'WBHO Construction',
  location: 'Polokwane, Limpopo',
  province: 'Limpopo',
  licenseRequired: 'Code 14 (EC)',
  pdpRequired: false,
  salary: 'R15,000 – R19,000/month',
  jobType: 'Contract (6 months)',
  postedDaysAgo: 7,
  urgent: true,
  description: 'Transport construction materials and aggregate for major infrastructure project in Limpopo.'
}];


// Only the 2 most urgent jobs shown on the front page
const frontPageJobs = allAvailableJobs.filter((j) => j.urgent).slice(0, 2);

export default function AvailableJobs() {
  const router = useRouter();

  const handleJobClick = (jobId: string) => {
    router.push(`/?apply_job=${jobId}#signup`);
  };

  return (
    <div className="mt-8">
      {/* Section Header */}
      <div className="flex items-center justify-start gap-3 mb-5">
        <div className="w-1 h-8 bg-amber-400 rounded-full" />
        <div>
          <h2 className="text-xl font-800 text-white tracking-wide uppercase">AVAILABLE JOBS...

          </h2>
          <p className="text-blue-200 text-xs mt-0.5">
            2 urgent driver positions — sign up to see all {allAvailableJobs.length} listings
          </p>
        </div>
      </div>

      {/* Jobs List — only 2 urgent */}
      <div className="space-y-3">
        {frontPageJobs.map((job) =>
        <button
          key={job.id}
          type="button"
          onClick={() => handleJobClick(job.id)}
          className="w-full text-left bg-white/10 hover:bg-white/20 border border-white/20 hover:border-amber-400/60 rounded-xl p-4 transition-all duration-200 group cursor-pointer">

            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-blue-600/40 flex items-center justify-center shrink-0 mt-0.5">
                  <Truck size={18} className="text-blue-200" />
                </div>
                <div className="flex-1 min-w-0 text-center">
                  <div className="flex items-center gap-2 flex-wrap mb-1 justify-center">
                    <span className="font-700 text-white text-sm leading-tight group-hover:text-amber-300 transition-colors">
                      {job.title}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-red-500/80 text-white text-[10px] font-700 px-2 py-0.5 rounded-full">
                      <Star size={9} /> URGENT
                    </span>
                  </div>
                  <p className="text-amber-300 font-700 text-xs whitespace-nowrap mb-1">{job.salary}</p>
                  <p className="text-blue-200 text-xs font-600 mb-2">{job.company}</p>
                  <p className="text-blue-100/70 text-xs leading-relaxed mb-2 line-clamp-2">
                    {job.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 justify-center">
                    <span className="flex items-center gap-1 text-blue-200 text-xs">
                      <MapPin size={11} /> {job.location}
                    </span>
                    <span className="flex items-center gap-1 text-blue-200 text-xs text-center">
                      <Briefcase size={11} /> {job.licenseRequired}
                      {job.pdpRequired && ' + PDP'}
                    </span>
                    <span className="flex items-center gap-1 text-blue-200 text-xs">
                      <Clock size={11} /> {job.jobType}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <ChevronRight
                size={16}
                className="text-white/40 group-hover:text-amber-400 group-hover:translate-x-1 transition-all" />

              </div>
            </div>
          </button>
        )}
      </div>

      {/* Locked more jobs teaser */}
      <div className="mt-3 bg-white/5 border border-white/15 rounded-xl p-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
          <Lock size={15} className="text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-xs font-700">
            +{allAvailableJobs.length - 2} more jobs available
          </p>
          <p className="text-blue-200 text-[11px] mt-0.5">
            Sign up &amp; pay R123 to unlock all listings in your dashboard
          </p>
        </div>
      </div>

      {/* CTA note */}
      <div className="mt-3 bg-amber-400/10 border border-amber-400/30 rounded-xl p-3 text-center">
        <p className="text-amber-300 text-xs font-600">🔒 Sign up & pay R92 once — we auto-apply to all matching jobs on your behalf

        </p>
      </div>
    </div>);

}